import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Save } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console for debugging
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    
    // Update state with error details
    this.setState({
      error,
      errorInfo
    });

    // Save current work to localStorage as backup
    try {
      const appState = localStorage.getItem('slideAppState');
      if (appState) {
        const timestamp = new Date().toISOString();
        localStorage.setItem(`slideAppState_backup_${timestamp}`, appState);
        console.log('Backup saved to localStorage');
      }
    } catch (backupError) {
      console.error('Failed to save backup:', backupError);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <div className="flex items-center mb-6">
              <AlertTriangle className="w-12 h-12 text-red-500 mr-4" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  앱에 오류가 발생했습니다
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  걱정하지 마세요. 작업 내용은 자동으로 저장되었습니다.
                </p>
              </div>
            </div>

            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-6">
              <p className="text-sm font-mono text-gray-800 dark:text-gray-200 mb-2">
                {this.state.error?.toString()}
              </p>
              {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                    상세 정보 보기
                  </summary>
                  <pre className="mt-2 text-xs overflow-auto max-h-40 text-gray-700 dark:text-gray-300">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>

            <div className="flex gap-4">
              <button
                onClick={this.handleReset}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
                다시 시도
              </button>
              <button
                onClick={this.handleReload}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                <Save className="w-5 h-5" />
                페이지 새로고침
              </button>
            </div>

            <div className="mt-6 text-sm text-gray-600 dark:text-gray-400">
              <p className="mb-2">문제가 계속 발생하면:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>브라우저 캐시를 지워보세요</li>
                <li>다른 브라우저에서 시도해보세요</li>
                <li>개발자 도구(F12)에서 콘솔 오류를 확인하세요</li>
              </ul>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}